/**
 * The superadmin's own session, stashed while they view PRISM as a customer (AdminConsole handleImpersonate).
 * Spec 2026-09-29-impersonation-read-only-and-audit §2.2: every way OUT of a view-as — Exit, an ended
 * session (403 impersonation_ended), or the 1 h view-as token expiring while the stash is present — puts
 * this session back and returns to /admin (a superadmin) or /settings/users (an appliance's org admin, spec
 * 2026-09-29-appliance-administration §4). It never signs anyone out.
 */
export const ORIGINAL_TOKEN_KEY = 'prism_impersonation_original_token';
export const ORIGINAL_USER_KEY = 'prism_impersonation_original_user';
export const NOTICE_KEY = 'prism_impersonation_notice';
export const ENDED_NOTICE = 'Your view-as session ended.';
export const READ_ONLY_COPY = "You're viewing as another user — changes are disabled.";

export function hasImpersonationStash(): boolean {
  return !!localStorage.getItem(ORIGINAL_TOKEN_KEY) && !!localStorage.getItem(ORIGINAL_USER_KEY);
}

export function clearImpersonationStash(): void {
  localStorage.removeItem(ORIGINAL_TOKEN_KEY);
  localStorage.removeItem(ORIGINAL_USER_KEY);
}

/** A JWT's payload, or null when the value is not a decodable JWT. The signature is NOT checked: this only
 * decides which session to put back, never what the server will accept. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const part = token.split('.')[1];
  if (!part) {
    return null;
  }
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Is the session in use RIGHT NOW an operator's view-as token (imp.by set, and not the user themself —
 * the same test as the backend guard)? A stash left behind by a closed tab must never be restored into
 * someone else's session just because THEIR token expired.
 */
export function currentTokenIsViewAs(): boolean {
  const token = sessionStorage.getItem('prism_token') || localStorage.getItem('prism_token');
  if (!token) {
    return false;
  }
  const payload = decodeJwtPayload(token);
  const imp = payload?.imp as { by?: unknown } | undefined;
  return !!payload && imp?.by != null && String(imp.by) !== String(payload.id);
}

// Set once a restore has begun in this page load (Exit or the interceptor): a request still in flight
// with the old view-as token that then fails as expired must not log out the restored superadmin.
let restoreStarted = false;

export function impersonationRestoreStarted(): boolean {
  return restoreStarted;
}

/**
 * Where Exit lands: the Admin Console for a superadmin, Administration for anyone else. An appliance's org admin
 * has no /admin, and a superadmin sent to /settings/users would read "Only an organisation administrator…".
 * Only an explicit `is_superadmin: true` in the stash is a superadmin (AdminConsole renders for nothing less).
 */
export function restoreDestination(stashedUser: string): '/admin' | '/settings/users' {
  try {
    const u: unknown = JSON.parse(stashedUser);
    return u !== null && typeof u === 'object' && (u as { is_superadmin?: unknown }).is_superadmin === true
      ? '/admin'
      : '/settings/users';
  } catch {
    return '/settings/users';
  }
}

/**
 * Start a view-as: stash the CURRENT session, switch to the view-as token, open the dashboard — the same steps as
 * AdminConsole.handleImpersonate, so piece 4's leftover-stash protections apply unchanged. False, and nothing
 * written, when there is no current session to stash: without one the banner could never offer Exit. False too,
 * and nothing written, when the current session is ITSELF a view-as token (defence in depth behind the backend
 * guard): stashing it would overwrite the admin's real session, and Exit would then return to the view-as.
 */
export function beginViewAs(token: string, user: unknown): boolean {
  const originalToken = sessionStorage.getItem('prism_token') || localStorage.getItem('prism_token');
  const originalUser = sessionStorage.getItem('prism_user') || localStorage.getItem('prism_user');
  if (!originalToken || !originalUser || currentTokenIsViewAs()) {
    return false;
  }
  localStorage.setItem(ORIGINAL_TOKEN_KEY, originalToken);
  localStorage.setItem(ORIGINAL_USER_KEY, originalUser);
  localStorage.setItem('prism_token', token);
  localStorage.setItem('prism_user', JSON.stringify(user));
  sessionStorage.removeItem('prism_token');
  sessionStorage.removeItem('prism_user');
  window.location.href = '/dashboard';
  return true;
}

/** Restore the stashed session and go to restoreDestination(). False (and nothing done) when there is no stash. */
export function restoreImpersonator(notice?: string): boolean {
  const token = localStorage.getItem(ORIGINAL_TOKEN_KEY);
  const user = localStorage.getItem(ORIGINAL_USER_KEY);
  if (!token || !user) {
    return false;
  }
  const destination = restoreDestination(user);
  restoreStarted = true;
  localStorage.setItem('prism_token', token);
  localStorage.setItem('prism_user', user);
  // The request interceptor reads sessionStorage FIRST; a leftover there would shadow the restore.
  sessionStorage.removeItem('prism_token');
  sessionStorage.removeItem('prism_user');
  clearImpersonationStash();
  if (notice) {
    localStorage.setItem(NOTICE_KEY, notice);
  }
  window.location.href = destination;
  return true;
}

/** The notice left by a restore, read once. */
export function takeImpersonationNotice(): string | null {
  const notice = localStorage.getItem(NOTICE_KEY);
  if (notice) {
    localStorage.removeItem(NOTICE_KEY);
  }
  return notice;
}
